import { clearCourseSetupRecovery } from "@/lib/course-setup-recovery";
import { clearGuidanceRecovery } from "@/components/courses/guidance-recovery";
import { clearInboxRecovery } from "@/components/inbox/inbox-recovery";
import { clearWritingRecovery } from "@/components/assignments/writing-recovery";
import { clearRollRecovery } from "@/components/attendance/roll-recovery";
import { clearTabDrafts, TabDraftOwnerProvider } from "@/components/ui/box-draft";
/**
 * Auth context (issue #5) — replaces the abandoned v1 `LmsAuthContext`.
 *
 * Identity comes from two sources, never PostgREST:
 *   - the GoTrue SESSION (supabase-js), and
 *   - the role/profile from the backend `GET /me` (membership-resolved).
 *
 * Vocabulary is the new `admin | instructor | student`.
 *
 * The supabase auth listener stays SYNCHRONOUS (it only sets session state) — a
 * separate effect loads `/me` when the signed-in user changes. This deliberately
 * avoids calling any `supabase.auth.*` method inside the `onAuthStateChange`
 * callback, which deadlocks supabase-js v2's internal lock.
 */
import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "@/lib/api/client";
import { supabase } from "@/lib/supabase/supabase";

export type Role = "admin" | "instructor" | "student";

/**
 * The last resolution of `GET /me` for the current credential.
 *
 * A session and a membership are two separate facts, and the pre-redemption
 * state is exactly the gap between them: Google has authenticated the person,
 * and `lms_membership` has not yet authorized them. The backend answers that
 * gap with a deliberate 403 (the Gate 0 outcome), so the frontend must be able
 * to name it. Collapsing it into "not signed in" sends a person holding a live
 * session back to the sign-in form, which is indistinguishable from a failed
 * sign-in and invites another OAuth round trip that lands in the same place.
 *
 * This is the LAST RESOLUTION, not the in-flight state: a refresh leaves the
 * previous value in place and raises `isLoading` instead, so a retry never
 * flickers a resolved screen back to "loading".
 *
 *   anonymous — no session; there is nothing to resolve
 *   loading   — a session exists and its first `/me` has not answered yet
 *   active    — an active membership backs the session
 *   none      — 403: the session is valid and carries no LMS authority
 *   error     — `/me` could not be resolved (transport, 401, tenant refusal)
 */
export type MembershipStatus = "anonymous" | "loading" | "active" | "none" | "error";

export interface Me {
  user_id: string;
  tenant_id: string;
  tenant_slug: string | null;
  role: Role;
  is_super_admin: boolean;
  /** The sign-in address of the login. */
  email: string | null;
  /**
   * The caller's person (ADR 0034), never the login account's name. Null while
   * the institution has not named the person.
   */
  full_name: string | null;
  honorific: string | null;
  preferred_name: string | null;
  workspaces: {
    registrar: { available: boolean };
    teaching: { available: boolean; course_count: number };
    learning: { available: boolean; enrollment_count: number };
  };
}

interface GoogleSignInOptions {
  selectAccount?: boolean;
  loginHint?: string;
}

interface AuthContextValue {
  session: Session | null;
  me: Me | null;
  role: Role | null;
  /**
   * Session AND active membership — the gate for member data and member routes.
   *
   * It is deliberately false for an authenticated caller with no membership.
   * Read `membershipStatus` to tell that apart from a signed-out visitor;
   * never infer "not signed in" from this flag alone.
   */
  isAuthenticated: boolean;
  /** The last `/me` resolution for the current credential. */
  membershipStatus: MembershipStatus;
  isLoading: boolean;
  isAuthReady: boolean;
  isRecovery: boolean;
  authInitializationError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithGoogle: (
    redirectTo?: string,
    options?: GoogleSignInOptions,
  ) => Promise<{ error: string | null }>;
  refreshMembership: () => Promise<boolean>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>;
  verifyRecoveryCode: (email: string, code: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const SIGN_OUT_CHANNEL = "imam:signed-out";

/**
 * Browser recovery copies (writing, guidance, inbox, the roll) end only with
 * their owner's explicit sign-out (ADR 0032). A lost session, a failed refresh
 * or a relaunch that opens signed out keeps them: each copy is bound to its
 * tenant and user, and is offered back only to that same login.
 */
function removeBrowserCopies() {
  clearCourseSetupRecovery();
  clearGuidanceRecovery();
  clearInboxRecovery();
  clearRollRecovery();
  clearTabDrafts();
  void clearWritingRecovery();
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatus>("loading");
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitializedAuth, setHasInitializedAuth] = useState(false);
  const [hasResolvedSession, setHasResolvedSession] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const [authInitializationError, setAuthInitializationError] = useState<string | null>(null);
  const profileRequest = useRef(0);

  const signOutChannel = useRef<BroadcastChannel | null>(null);

  // Another tab of this origin signed out: its own copies are already gone,
  // and this tab removes its per-tab copies and stops its editors writing more.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(SIGN_OUT_CHANNEL);
    channel.addEventListener("message", removeBrowserCopies);
    signOutChannel.current = channel;
    return () => {
      signOutChannel.current = null;
      channel.close();
    };
  }, []);

  const loadMembership = useCallback(async (): Promise<boolean> => {
    const request = ++profileRequest.current;
    setIsLoading(true);
    try {
      const profile = await api.get<Me>("/me");
      if (request === profileRequest.current) {
        setMe(profile);
        setMembershipStatus("active");
      }
      return true;
    } catch (err) {
      if (request === profileRequest.current) {
        setMe(null);
        // 403 is the designed pre-redemption answer: authenticated, and not yet
        // authorized. Every other failure leaves the membership unknown rather
        // than known-absent, so it must not be presented as "not activated".
        const refused = err instanceof ApiError && err.status === 403;
        setMembershipStatus(refused ? "none" : "error");
        if (!(err instanceof ApiError)) console.error("Failed to load /me", err);
      }
      return false;
    } finally {
      if (request === profileRequest.current) setIsLoading(false);
    }
  }, []);

  // Session lifecycle. The callback is synchronous (no supabase.auth.* calls).
  useEffect(() => {
    let mounted = true;
    let receivedAuthEvent = false;
    let subject: string | null = null;

    const acceptSession = (next: Session | null) => {
      const nextSubject = next?.user.id ?? null;
      if (subject !== nextSubject) {
        // A new Google account has not inherited the previous account's /me.
        // Withdraw its role and cached reads before resolving the new subject.
        // Recovery copies remain bound to their owner until explicit sign-out.
        profileRequest.current += 1;
        queryClient.clear();
        setMe(null);
        setMembershipStatus(nextSubject ? "loading" : "anonymous");
        setIsLoading(!!nextSubject);
        setIsRecovery(false);
      }
      subject = nextSubject;
      setSession(next);
      setHasResolvedSession(true);
    };

    // GoTrue auto-initializes when the client is constructed. Calling
    // initialize() again returns that same promise and lets the UI distinguish
    // an invalid callback from a session that is still being exchanged.
    void supabase.auth
      .initialize()
      .then(({ error }) => {
        if (mounted) setAuthInitializationError(error?.message ?? null);
      })
      .catch((error: unknown) => {
        if (mounted) {
          setAuthInitializationError(
            error instanceof Error ? error.message : "Authentication could not be initialized.",
          );
        }
      })
      .finally(() => {
        if (mounted) setHasInitializedAuth(true);
      });

    void supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        // A cross-tab sign-in may settle before the initial persisted read.
        // Its newer subject must not be replaced by that earlier snapshot.
        if (mounted && !receivedAuthEvent) acceptSession(s);
      })
      .catch((error: unknown) => {
        if (mounted && !receivedAuthEvent) {
          acceptSession(null);
          setAuthInitializationError(
            error instanceof Error ? error.message : "Authentication could not be initialized.",
          );
        }
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      receivedAuthEvent = true;
      acceptSession(s);
      if (event === "PASSWORD_RECOVERY") setIsRecovery(true);
      if (event === "SIGNED_OUT") {
        profileRequest.current += 1;
        queryClient.clear();
        setIsRecovery(false);
        setMe(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // Resolve role/profile when the signed-in user or session credential changes.
  // A fresh token must retry /me even when a prior request for the same user failed.
  const userId = session?.user?.id ?? null;
  const accessToken = session?.access_token ?? null;
  useEffect(() => {
    if (!hasResolvedSession) return;
    if (!userId) {
      profileRequest.current += 1;
      setMe(null);
      setMembershipStatus("anonymous");
      setIsLoading(false);
      return;
    }
    // Exactly one automatic read per credential, enforced by the dependency
    // list: a re-emitted session (tab focus, cross-tab sync) carries the same
    // token and cannot re-enter here. A 403 therefore stays asked-and-answered
    // for the life of that token, and only a genuinely new one asks again.
    // Redemption grants membership WITHOUT minting a new token, which is why
    // `refreshMembership` calls the loader directly rather than through here.
    void loadMembership();
    return () => {
      profileRequest.current += 1;
    };
  }, [accessToken, hasResolvedSession, loadMembership, userId]);

  const signIn: AuthContextValue["signIn"] = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signInWithGoogle: AuthContextValue["signInWithGoogle"] = async (redirectTo, options) => {
    const queryParams: Record<string, string> = {
      ...(options?.selectAccount ? { prompt: "select_account" } : {}),
      ...(options?.loginHint ? { login_hint: options.loginHint } : {}),
    };
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: redirectTo ?? window.location.origin,
        ...(Object.keys(queryParams).length > 0 ? { queryParams } : {}),
      },
    });
    return { error: error?.message ?? null };
  };

  const signOut: AuthContextValue["signOut"] = async () => {
    removeBrowserCopies();
    signOutChannel.current?.postMessage("signed-out");
    await supabase.auth.signOut();
    setMe(null);
    setMembershipStatus("anonymous");
    // CRITICAL (issue #16 review finding 1): clear the whole React Query
    // cache on sign-out. AuthProvider renders inside QueryClientProvider, so
    // this is available. Without it, a same-tenant sign-out-and-different-
    // user-sign-in in the same tab would serve the first user's cached
    // dashboard data to the second — the cache lives outside the auth
    // lifecycle and is never otherwise invalidated on identity change.
    clearGuidanceRecovery();
    queryClient.clear();
  };

  const requestPasswordReset: AuthContextValue["requestPasswordReset"] = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error?.message ?? null };
  };

  const verifyRecoveryCode: AuthContextValue["verifyRecoveryCode"] = async (email, code) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: "recovery",
    });
    return { error: error?.message ?? null };
  };

  const updatePassword: AuthContextValue["updatePassword"] = async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) setIsRecovery(false);
    return { error: error?.message ?? null };
  };

  const value: AuthContextValue = {
    session,
    me,
    role: me?.role ?? null,
    isAuthenticated: !!session && !!me,
    membershipStatus,
    isLoading,
    isAuthReady: hasInitializedAuth && hasResolvedSession,
    isRecovery,
    authInitializationError,
    signIn,
    signInWithGoogle,
    refreshMembership: loadMembership,
    signOut,
    requestPasswordReset,
    verifyRecoveryCode,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* A box's browser draft is kept under this login and tenant only, and waits while the login resolves (CASE-038). */}
      <TabDraftOwnerProvider owner={me ? { tenant: me.tenant_id, user: me.user_id } : membershipStatus === "loading" ? undefined : null}>{children}</TabDraftOwnerProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
